import React, { useState, useEffect, useMemo } from 'react';
import { X, Search, RefreshCw, Tag, Link } from 'lucide-react';
import { categoryFieldAPI } from '../../services/api';

const AttachFieldDialog = ({ 
  isOpen, 
  onClose, 
  onAttachField, 
  itemId, 
  itemType,
  template_content,
  template_category_id,
  currentAttachedField = null 
}) => {
  console.log('🎯 AttachFieldDialog component rendered with props:', {
    isOpen,
    onClose,
    onAttachField,
    itemId,
    itemType,
    template_content,
    template_category_id,
    currentAttachedField
  });
  const [categoryFields, setCategoryFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryInfo, setCategoryInfo] = useState(null);

  // Fetch fields when dialog opens
  useEffect(() => {
    if (isOpen) {
      fetchCategoryFields();
      fetchCategoryInfo();
    }
  }, [isOpen, template_category_id, template_content?.subcategory_id]);

  const fetchCategoryFields = async () => {
    if (!template_category_id) {
      setCategoryFields([]);
      return;
    }

    setLoading(true);
    try {
      const response = await categoryFieldAPI.listForCategory(template_category_id);
      const data = response.data;
      setCategoryFields(data.data || []);
    } catch (error) {
      console.error('Error fetching category fields:', error);
      setCategoryFields([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategoryInfo = async () => {
    if (!template_category_id) {
      setCategoryInfo(null);
      return;
    }

    try {
      // Import categoryAPI if not already imported
      const { categoryAPI } = await import('../../services/api');
      const response = await categoryAPI.getById(template_category_id);
      if (response.data.success) {
        setCategoryInfo(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching category info:', error);
      setCategoryInfo(null);
    }
  };



  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchCategoryFields();
    } finally {
      setRefreshing(false);
    }
  };

  const handleAttachField = (field, event) => {
    if (event) {
      event.stopPropagation();
    }
    console.log('🔗 AttachFieldDialog: Attaching field:', { itemId, field });
    onAttachField(itemId, field.id, field, false);
    onClose();
  };

  const isFieldCompatible = (field) => {
    // Define field type compatibility with item types
    const compatibility = {
      'text': ['text', 'textarea', 'select', 'multiselect', 'radio', 'checkbox', 'url', 'email', 'phone', 'color', 'date', 'time', 'datetime', 'number', 'range', 'password', 'multiselect-image'],
      'image': ['image', 'file', 'multiselect-image'],
      'video': ['video', 'file', 'url', 'multiselect-image'],
      'audio': ['audio', 'file', 'url', 'multiselect-image'],
      'sound-effect': ['audio', 'file', 'url', 'multiselect-image'],
      'tts': ['text', 'textarea', 'multiselect-image']
    };

    return compatibility[itemType]?.includes(field.field_type || field.fieldType) || false;
  };

  // Filter fields based on search and compatibility
  const filteredCategoryFields = categoryFields.filter(field => {
    const matchesSearch = field.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         field.key.toLowerCase().includes(searchTerm.toLowerCase());
    const isCompatible = isFieldCompatible(field);
    return matchesSearch && isCompatible;
  });

  const attachedFieldStats = useMemo(() => {
    const stats = {};

    const registerField = (fieldId) => {
      if (!fieldId) return;
      const key = String(fieldId);
      if (!stats[key]) {
        stats[key] = 0;
      }
      stats[key] += 1;
    };

    const visitItem = (item) => {
      if (!item) return;
      registerField(item.categoryFieldId || item.categoryField?.id);

      if (item.isPowerClipContainer && Array.isArray(item.powerClipContents)) {
        item.powerClipContents.forEach(visitItem);
      }
    };

    (template_content?.pages || []).forEach(page => {
      (page.items || []).forEach(visitItem);
    });

    return stats;
  }, [template_content]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center space-x-2">
            <Link className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold">Attach Field to {itemType} Item</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Controls */}
        <div className="p-4 border-b space-y-3">
          {/* Search and Refresh */}
          <div className="flex items-center space-x-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search fields..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-1"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          </div>

          {/* Category Info Display */}
          {categoryInfo && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center space-x-2">
                <Tag className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-800">
                  Category: {categoryInfo.name}
                </span>
                <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                  {filteredCategoryFields.length} fields available
                </span>
              </div>
              {categoryInfo.description && (
                <p className="text-xs text-blue-600 mt-1">{categoryInfo.description}</p>
              )}
            </div>
          )}
        </div>

        {/* Current Attached Field */}
        {currentAttachedField && (
          <div className="p-4 bg-green-50 border-b">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Tag className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-green-800">
                  Currently Attached: {currentAttachedField.label}
                </span>
                <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded">
                  {currentAttachedField.field_type || currentAttachedField.fieldType}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Fields List */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-gray-600">Loading fields...</span>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredCategoryFields.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Tag className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>No compatible category fields found</p>
                  <p className="text-sm">
                    {!template_category_id
                      ? 'This template has no category assigned'
                      : 'Try adjusting your search or check field types'
                    }
                  </p>
                </div>
              ) : (
                filteredCategoryFields.map(field => {
                  const fieldId = field.id || field.field_id;
                  const totalAttachments = attachedFieldStats[String(fieldId)] || 0;
                  const isAttachedAnywhere = totalAttachments > 0;
                  const isAttachedToCurrent = currentAttachedField?.id === fieldId;

                  return (
                  <div
                    key={field.id}
                    className="border rounded-lg p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={(e) => handleAttachField(field, e)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <h3 className="font-medium text-gray-900">{field.label}</h3>
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                            {field.field_type || field.fieldType}
                          </span>
                          {isAttachedToCurrent && (
                            <span className="text-[11px] px-2 py-0.5 rounded-full bg-green-100 text-green-700 border border-green-200">
                              Attached to this item
                            </span>
                          )}
                          {!isAttachedToCurrent && isAttachedAnywhere && (
                            <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200">
                              {totalAttachments === 1 ? 'Attached' : `Attached ×${totalAttachments}`}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">Key: {field.key}</p>
                        {field.description && (
                          <p className="text-xs text-gray-500 mt-1">{field.description}</p>
                        )}
                      </div>
                      <button
                        onClick={(e) => handleAttachField(field, e)}
                        className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                      >
                        Attach
                      </button>
                    </div>
                  </div>
                )})
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Compatible with <span className="font-medium">{itemType}</span> items
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttachFieldDialog;
